import React from 'react';
import { Formik, ErrorMessage, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { addBalanceRecord } from '../../slices/employeeBalance';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { convertDatePickerToDate } from '../../utilities/dateUtils';
import DatePickerWrapper from './FormComponents/DatePickerWrapper';
import BalanceInput from './FormComponents/BalanceInput';

const AddBalanceForm = ({ userId, onSuccess, lastBalanceStartDate }) => {
  const dispatch = useDispatch();
  const { submitting, error } = useSelector((state) => state.employeeBalance);

  const getLastBalanceDateForPicker = () => {
    if (!lastBalanceStartDate) return null;
    return lastBalanceStartDate;
  };

  const initialValues = {
    startDate: '',
    balance: ''
  };
  
  const validationSchema = Yup.object({
    startDate: Yup.object().nullable().required('Start Date is required field'),
    balance: Yup.number()
      .required('Balance is required field')
      .positive('Balance must be a positive number')
      .max(99.99, 'Balance must be less than 99.99')
      .typeError('Balance must be a number')
  });

  const handleSubmit = async (values, { resetForm }) => {
    try {
      await dispatch(addBalanceRecord({ 
        userId, 
        balanceData: {
          ...values,
          startDate: convertDatePickerToDate(values.startDate)
        } 
      })).unwrap();
      
      toast.success('Balance record added successfully!');
      resetForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error|| 'Failed to add balance record');
    }
  };

  const handleKeyDown = (e, formik) => {
    if (e.key === 'Enter' && formik.isValid && !formik.isSubmitting && !submitting) {
      e.preventDefault();
      formik.handleSubmit();
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <div className="employee-balance-add-form" onKeyDown={(e) => handleKeyDown(e, formik)}>
          <div className="employee-balance-form-group">
            <label htmlFor="balance">Start Date*</label>
            <DatePickerWrapper
              name="startDate"
              placeholder="Select start date"
              calendarPopperPosition="bottom"
              disableBeforeDate={!!lastBalanceStartDate}
              minimumDateValue={getLastBalanceDateForPicker()}
            />
            <ErrorMessage name="startDate" component="div" className="employee-balance-error-message" />
          </div>

          <div className="employee-balance-form-group">
            <label htmlFor="balance">Balance (Days) *</label>
            <BalanceInput
              type="number"
              name="balance"
              min="0"
              max="99.99"
              step="0.01"
              className="employee-balance-form-control"
              placeholder="Enter balance (e.g. 16.55)"
            />
            <ErrorMessage name="balance" component="div" className="employee-balance-error-message" />
          </div>

          <div className="employee-balance-form-actions">
            <button 
              type="button"
              className="employee-balance-submit-btn"
              disabled={formik.isSubmitting || submitting}
              onClick={formik.handleSubmit}
            >
              {formik.isSubmitting || submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default AddBalanceForm;